import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Loader2, Search, Sparkles, User, Briefcase, Mail, Linkedin, Lock, Edit, Clock, UserPlus } from "lucide-react";
import { ProfileDetailDialog } from "@/components/directory/ProfileDetailDialog";
import { ConnectionButton } from "@/components/connections/ConnectionButton";
import { MentorshipRequestDialog } from "@/components/mentorship/MentorshipRequestDialog";

import { RequestMentorshipByTopicDialog } from "@/components/mentorship/RequestMentorshipByTopicDialog";
import { ConnectionsPreview } from "@/components/directory/ConnectionsPreview";
import { UserName } from "@/components/badges/UserName";
import { ConnectionBanner } from "@/components/directory/ConnectionBanner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useCreateConnectionRequest } from "@/hooks/useConnections";
import { useExistingMentorshipRequest } from "@/hooks/useMentorshipRequests";
import { useFoundationsEligibility } from "@/hooks/useFoundationsEligibility";
import { useCommunityConnections } from "@/hooks/useCommunityConnections";
import { EDUCATION_LEVEL_OPTIONS, getEducationLabel } from "@/lib/educationLevels";
import { COMPANY_STAGE_OPTIONS, getCompanyStageLabel } from "@/lib/companyStages";
import { getStorageUrl } from "@/lib/storage";

interface MemberProfile {
  id: string;
  full_name: string;
  avatar_url: string | null;
  bio: string | null;
  role: string;
  email?: string | null;
  linkedin_url?: string | null;
  parish?: string | null;
  phone?: string | null;
  phone_type?: string | null;
  referred_by?: string | null;
  gender?: string | null;
  age_range?: string | null;
  has_device?: string | null;
  education_level?: string | null;
  is_published?: boolean;
  is_verified?: boolean;
  is_onboarded?: boolean;
  is_mentor?: boolean;
  is_entrepreneur?: boolean;
  skills: { name: string }[];
  industries: { name: string }[];
  entrepreneur_profile?: {
    company_name: string | null;
    company_description: string | null;
    startup_stage: string | null;
    target_market: string | null;
    funding_status: string | null;
    specific_challenges: string | null;
  } | null;
  mentor_profile?: {
    years_experience: number | null;
    professional_background: string | null;
    career_highlights: string | null;
    availability_notes: string | null;
  } | null;
}

export default function Directory() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<'first_name' | 'last_name' | 'connected_first'>('first_name');
  const [selectedMember, setSelectedMember] = useState<MemberProfile | null>(null);
  const [requestMessage, setRequestMessage] = useState("");
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showMentorshipDialog, setShowMentorshipDialog] = useState(false);
  const [viewingMember, setViewingMember] = useState<MemberProfile | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlProfileId = searchParams.get("profile");
  const [handledProfileParam, setHandledProfileParam] = useState<string | null>(null);
  const [editingMember, setEditingMember] = useState<MemberProfile | null>(null);
  const [mentorshipTarget, setMentorshipTarget] = useState<MemberProfile | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "mentors" | "entrepreneurs">("all");
  const [showTopicRequestDialog, setShowTopicRequestDialog] = useState(false);
  const [showConnectedOnly, setShowConnectedOnly] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  // Check if user is admin via user_roles table (not profile.role)
  const { data: isAdmin } = useQuery({
    queryKey: ["is_admin_directory", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data } = await supabase.rpc("has_role", { _user_id: user.id, _role: "admin" });
      return data;
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // Admin status rarely changes - cache for 10 minutes
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const profileReady = !profileLoading && !!profile;
  const isEntrepreneur = profileReady && profile?.role === "entrepreneur";
  const isMentor = profileReady && profile?.role === "mentor";

  // Get weekly requests count for entrepreneurs
  const { data: weeklyRequests } = useQuery({
    queryKey: ["weekly_requests", user?.id],
    queryFn: async () => {
      if (!user?.id || !isEntrepreneur) return 0;
      const { data } = await supabase.rpc("get_weekly_pending_requests", { _user_id: user.id });
      return data as number;
    },
    enabled: !!user?.id && isEntrepreneur,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes - changes when user makes requests
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Check if connection limit is enabled
  const { data: connectionLimitSetting } = useQuery({
    queryKey: ["site_setting_connection_limit"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "connection_limit_enabled")
        .single();
      
      if (error && error.code !== "PGRST116") throw error;
      return data?.value === "true";
    },
    staleTime: 10 * 60 * 1000, // Site settings rarely change - cache for 10 minutes
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const connectionLimitEnabled = connectionLimitSetting ?? true; // Default to enabled

  // Determine if viewer can see contact info
  // Mentors can see entrepreneur contact info
  // Entrepreneurs cannot see mentor contact info
  // Admins can see all contact info
  const canViewContactInfo = (targetRole: string) => {
    if (isAdmin) return true;
    if (isMentor && targetRole === "entrepreneur") return true;
    return false;
  };

  // Fetch directory members based on active tab
  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ["directory", activeTab, searchQuery, profile?.role],
    queryFn: async () => {
      const targetRole = activeTab === "mentors" ? "mentor" : activeTab === "entrepreneurs" ? "entrepreneur" : null;
      
      let query = supabase
        .from("profiles")
        .select(`
          id,
          full_name,
          avatar_url,
          bio,
          role,
          referred_by,
          gender,
          age_range,
          has_device,
          education_level,
          is_published,
          is_verified,
          is_mentor,
          is_entrepreneur,
          user_skills(skills(name)),
          user_industries(industries(name))
        `);

      // Admins can see all profiles (published or not), others only see published
      if (!isAdmin) {
        query = query.eq("is_published", true);
      }

      // Filter by role if not "all"
      if (targetRole) {
        query = query.eq("role", targetRole);
      } else {
        // For "all" tab, exclude admins
        query = query.in("role", ["mentor", "entrepreneur"]);
      }

      if (searchQuery) {
        // Also match by company name (entrepreneur_profiles.company_name)
        const { data: companyMatches } = await supabase
          .from("entrepreneur_profiles")
          .select("id")
          .ilike("company_name", `%${searchQuery}%`);
        const companyMatchIds = (companyMatches || []).map((r: any) => r.id);

        const escaped = searchQuery.replace(/[,()]/g, " ");
        if (companyMatchIds.length > 0) {
          query = query.or(
            `full_name.ilike.%${escaped}%,id.in.(${companyMatchIds.join(",")})`
          );
        } else {
          query = query.ilike("full_name", `%${searchQuery}%`);
        }
      }

      const { data: profiles, error } = await query.limit(50);
      if (error) throw error;

      // Fetch role-specific profiles
      const mentorIds = profiles?.filter((p) => p.role === "mentor").map((p) => p.id) || [];
      const entrepreneurIds = profiles?.filter((p) => p.role === "entrepreneur").map((p) => p.id) || [];
      const allProfileIds = profiles?.map((p) => p.id) || [];

      let mentorProfiles: any[] = [];
      let entrepreneurProfiles: any[] = [];
      let privateData: any[] = [];

      if (mentorIds.length > 0) {
        const { data } = await supabase.from("mentor_profiles").select("*").in("id", mentorIds);
        mentorProfiles = data || [];
      }
      
      if (entrepreneurIds.length > 0) {
        const { data } = await supabase.from("entrepreneur_profiles").select("*").in("id", entrepreneurIds);
        entrepreneurProfiles = data || [];
      }

      // Fetch private data if admin or mentor viewing entrepreneurs
      const shouldFetchPrivate = isAdmin || (isMentor && entrepreneurIds.length > 0);
      const privateIdsToFetch = isAdmin ? allProfileIds : entrepreneurIds;
      
      if (shouldFetchPrivate && privateIdsToFetch.length > 0) {
        const { data } = await supabase.from("profile_private").select("*").in("user_id", privateIdsToFetch);
        privateData = data || [];
      }

      return profiles?.map((p) => {
        const includeAllData = isAdmin;
        const canSeeContact = isAdmin || (isMentor && p.role === "entrepreneur");
        const privData = privateData.find((pd) => pd.user_id === p.id);
        return {
          id: p.id,
          full_name: p.full_name,
          avatar_url: p.avatar_url,
          bio: p.bio,
          role: p.role,
          // Contact info from profile_private (admin or mentor viewing entrepreneur)
          email: canSeeContact ? privData?.email : null,
          linkedin_url: canSeeContact ? privData?.linkedin_url : null,
          parish: canSeeContact ? privData?.parish : null,
          phone: canSeeContact ? privData?.phone : null,
          phone_type: canSeeContact ? privData?.phone_type : null,
          // Remaining fields from profiles
          referred_by: includeAllData ? p.referred_by : null,
          gender: includeAllData ? p.gender : null,
          age_range: includeAllData ? p.age_range : null,
          has_device: includeAllData ? p.has_device : null,
          education_level: includeAllData ? p.education_level : null,
          is_published: includeAllData ? p.is_published : undefined,
          is_verified: includeAllData ? p.is_verified : undefined,
          is_mentor: p.is_mentor,
          is_entrepreneur: p.is_entrepreneur,
          skills: p.user_skills?.map((us: any) => us.skills) || [],
          industries: p.user_industries?.map((ui: any) => ui.industries) || [],
          mentor_profile: p.role === "mentor" ? mentorProfiles.find((rp) => rp.id === p.id) : null,
          entrepreneur_profile: p.role === "entrepreneur" ? entrepreneurProfiles.find((rp) => rp.id === p.id) : null,
        };
      }) as MemberProfile[];
    },
    enabled: !!user?.id && !!profile,
    staleTime: 5 * 60 * 1000, // Cache directory for 5 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch connections from connections table (unified system)
  const { data: existingConnections } = useQuery({
    queryKey: ["user_connections", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("connections")
        .select("id, requester_id, recipient_id, status")
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .in("status", ["pending", "accepted"]);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch community connections (new connections table)
  const { data: communityConnections } = useCommunityConnections();

  // Helper to get connection status with another user (checks both tables)
  const getConnectionStatus = (memberId: string): { hasConnection: boolean; status: string | null; isConnected: boolean } => {
    // Never show connection status for own profile
    if (memberId === user?.id) return { hasConnection: false, status: null, isConnected: false };
    
    // Check new connections table first
    const communityConnection = communityConnections?.find(
      (c) => c.requester_id === memberId || c.recipient_id === memberId
    );
    
    if (communityConnection) {
      return { 
        hasConnection: true, 
        status: communityConnection.status, 
        isConnected: communityConnection.status === "accepted" 
      };
    }
    
    // Fall back to old connection_requests table
    const connection = existingConnections?.find(
      (c) => c.requester_id === memberId || c.recipient_id === memberId
    );
    
    if (connection) {
      return { 
        hasConnection: true, 
        status: connection.status, 
        isConnected: connection.status === "accepted" 
      };
    }
    return { hasConnection: false, status: null, isConnected: false };
  };

  // Get suggested matches for entrepreneurs
  const { data: suggestedMatches } = useQuery({
    queryKey: ["suggested_matches", user?.id],
    queryFn: async () => {
      if (!user?.id || !isEntrepreneur) return [];

      // Get entrepreneur's desired skills
      const { data: desiredSkills } = await supabase
        .from("user_skills")
        .select("skill_id")
        .eq("user_id", user.id);

      const { data: userIndustries } = await supabase
        .from("user_industries")
        .select("industry_id")
        .eq("user_id", user.id);

      if (!desiredSkills?.length && !userIndustries?.length) return [];

      const skillIds = desiredSkills?.map((s) => s.skill_id) || [];
      const industryIds = userIndustries?.map((i) => i.industry_id) || [];

      // Find mentors with matching skills/industries
      // Select skill/industry IDs as well as names. The scorer compares IDs, so
      // embedding only `name` made every mentorSkillIds entry undefined and every
      // match score 0.
      // Also: .limit(5) used to run BEFORE scoring, so five arbitrary mentors were
      // scored instead of the best five. Score the full candidate set, then slice.
      let mentorQuery = supabase
        .from("profiles")
        .select(`
          id,
          full_name,
          avatar_url,
          bio,
          role,
          user_skills(skill_id, skills(id, name)),
          user_industries(industry_id, industries(id, name))
        `)
        .eq("role", "mentor")
        .eq("is_published", true);

      const { data: mentors } = await mentorQuery;

      // Calculate match scores
      const scoredMentors = mentors?.map((mentor) => {
        const mentorSkillIds = mentor.user_skills?.map((us: any) => us.skill_id ?? us.skills?.id) || [];
        const mentorIndustryIds = mentor.user_industries?.map((ui: any) => ui.industry_id ?? ui.industries?.id) || [];

        const skillMatches = skillIds.filter((id) => mentorSkillIds.includes(id)).length;
        const industryMatches = industryIds.filter((id) => mentorIndustryIds.includes(id)).length;

        return {
          ...mentor,
          skills: mentor.user_skills?.map((us: any) => us.skills) || [],
          industries: mentor.user_industries?.map((ui: any) => ui.industries) || [],
          matchScore: skillMatches * 2 + industryMatches,
        };
      });

      // Rank the whole set, then take the top five, and drop non-matches so the
      // section shows genuine "suggested matches" rather than arbitrary mentors.
      return (
        scoredMentors
          ?.filter((m) => m.matchScore > 0)
          .sort((a, b) => b.matchScore - a.matchScore)
          .slice(0, 5) || []
      );
    },
    enabled: !!user?.id && isEntrepreneur,
    staleTime: 5 * 60 * 1000, // Cache suggested matches for 5 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Use the new hook for creating connection requests
  const createConnectionMutation = useCreateConnectionRequest();

  // Check for existing mentorship request with the currently viewed member
  const { data: existingMentorshipRequest } = useExistingMentorshipRequest(viewingMember?.id ?? null);

  // Check Foundations course eligibility for entrepreneurs
  const {
    canRequestMentorship: foundationsCompleted,
    isEnrolled: isEnrolledInFoundations,
    progress: foundationsProgress,
    courseLink: foundationsCourseLink,
  } = useFoundationsEligibility();

  // Handler for requesting mentorship
  const handleRequestMentorship = (member: MemberProfile) => {
    setMentorshipTarget(member);
    setShowMentorshipDialog(true);
    setShowProfileDialog(false);
  };



  const updateProfileMutation = useMutation({
    mutationFn: async (updates: {
      profileId: string;
      profileData: any;
      mentorData?: any;
      entrepreneurData?: any;
    }) => {
      const { profileId, profileData, mentorData, entrepreneurData } = updates;

      // Update main profile
      const { error: profileError } = await supabase
        .from("profiles")
        .update(profileData)
        .eq("id", profileId);

      if (profileError) throw profileError;

      // Update mentor profile if applicable
      if (mentorData) {
        const { data: existing } = await supabase
          .from("mentor_profiles")
          .select("id")
          .eq("id", profileId)
          .maybeSingle();

        if (existing) {
          const { error } = await supabase
            .from("mentor_profiles")
            .update(mentorData)
            .eq("id", profileId);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("mentor_profiles")
            .insert({ id: profileId, ...mentorData });
          if (error) throw error;
        }
      }

      // Update entrepreneur profile if applicable
      if (entrepreneurData) {
        const { data: existing } = await supabase
          .from("entrepreneur_profiles")
          .select("id")
          .eq("id", profileId)
          .maybeSingle();

        if (existing) {
          const { error } = await supabase
            .from("entrepreneur_profiles")
            .update(entrepreneurData)
            .eq("id", profileId);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("entrepreneur_profiles")
            .insert({ id: profileId, ...entrepreneurData });
          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["directory"] });
      toast.success("Profile updated successfully");
      setShowEditDialog(false);
      setEditingMember(null);
    },
    onError: (error) => {
      toast.error("Failed to update profile: " + error.message);
    },
  });

  const handleRequestConnection = (member: MemberProfile) => {
    // Prevent self-connection (same user ID)
    if (member.id === user?.id) {
      toast.error("You cannot connect with yourself.");
      return;
    }
    
    
    // Only apply limit for cross-role connections (entrepreneur requesting mentor)
    // Same-role connections (mentor-to-mentor, entrepreneur-to-entrepreneur) are never limited
    const isSameRoleConnection = profile?.role === member.role;
    
    // Check if weekly requests data has loaded before validating limits
    if (!isSameRoleConnection && connectionLimitEnabled && isEntrepreneur) {
      if (weeklyRequests === undefined) {
        toast.error("Please wait while we load your request status...");
        return;
      }
      if (weeklyRequests >= 3) {
        toast.error("You've reached your weekly limit of 3 connection requests.");
        return;
      }
    }
    setSelectedMember(member);
    setShowRequestDialog(true);
  };

  const handleAdminEdit = (member: MemberProfile) => {
    setEditingMember(member);
    setShowEditDialog(true);
  };

  const handleViewProfile = (member: MemberProfile) => {
    setViewingMember(member);
    setShowProfileDialog(true);
  };

  // Deep link support: "View Profile" elsewhere in the app navigates to
  // /directory?profile=<id>. This page never read the query string, so those
  // links silently landed on the directory with nothing open.
  useEffect(() => {
    if (!urlProfileId || urlProfileId === handledProfileParam) return;
    if (!members || members.length === 0) return;

    const match = members.find((m) => m.id === urlProfileId);
    setHandledProfileParam(urlProfileId);

    if (match) {
      handleViewProfile(match);
    } else {
      toast.error("That profile isn't available in the directory.");
    }

    // Clear the param so closing the dialog doesn't immediately reopen it.
    const next = new URLSearchParams(searchParams);
    next.delete("profile");
    setSearchParams(next, { replace: true });
  }, [urlProfileId, handledProfileParam, members, searchParams, setSearchParams]);

  const submitRequest = () => {
    if (!selectedMember || !requestMessage.trim()) {
      toast.error("Please enter a message explaining why you'd like to connect.");
      return;
    }
    
    // Determine if this is an E->M request that needs admin review
    const isAdminReviewRequired = profile?.role === "entrepreneur" && (selectedMember.role === "mentor" || selectedMember.is_mentor === true);
    
    createConnectionMutation.mutate(
      { recipientId: selectedMember.id, message: requestMessage },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["weekly_requests"] });
          queryClient.invalidateQueries({ queryKey: ["user_connections"] });
          const successMessage = isAdminReviewRequired
            ? "Connection request sent! An administrator will review it soon."
            : "Connection request sent!";
          toast.success(successMessage);
          setShowRequestDialog(false);
          setRequestMessage("");
          setSelectedMember(null);
        },
      }
    );
  };

  const remainingRequests = 3 - (weeklyRequests || 0);
  const showRemainingRequests = isEntrepreneur && connectionLimitEnabled;

  // Sort and filter members
  const sortedMembers = useMemo(() => {
    if (!members) return [];
    
    // First filter by connected only if enabled
    let filtered = showConnectedOnly 
      ? members.filter(m => getConnectionStatus(m.id).isConnected)
      : members;
    
    // Then sort
    return [...filtered].sort((a, b) => {
      if (sortBy === 'connected_first') {
        const aConnected = getConnectionStatus(a.id).isConnected ? 0 : 1;
        const bConnected = getConnectionStatus(b.id).isConnected ? 0 : 1;
        if (aConnected !== bConnected) return aConnected - bConnected;
        // Fall back to first name sort for ties
        const aFirst = a.full_name?.split(' ')[0] || '';
        const bFirst = b.full_name?.split(' ')[0] || '';
        return aFirst.localeCompare(bFirst);
      } else if (sortBy === 'first_name') {
        const aFirst = a.full_name?.split(' ')[0] || '';
        const bFirst = b.full_name?.split(' ')[0] || '';
        return aFirst.localeCompare(bFirst);
      } else {
        const aLast = a.full_name?.split(' ').slice(-1)[0] || '';
        const bLast = b.full_name?.split(' ').slice(-1)[0] || '';
        return aLast.localeCompare(bLast);
      }
    });
  }, [members, sortBy, showConnectedOnly, communityConnections, existingConnections, user?.id]);

  // Handle view profile from connections preview
  const handleViewProfileById = (userId: string) => {
    const member = members?.find(m => m.id === userId);
    if (member) {
      handleViewProfile(member);
    }
  };

  return (
    <DashboardLayout>
      {(authLoading || profileLoading) ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (!user || !profile) ? null : (
      <>
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Directory</h1>
            <p className="text-muted-foreground">
              Browse mentors and entrepreneurs in the EAB Connect community
            </p>
          </div>
          {showRemainingRequests && (
            <Badge variant="outline" className="text-sm">
              {remainingRequests} mentor requests remaining this week
            </Badge>
          )}
        </div>

        {/* Connections Preview Section */}
        <ConnectionsPreview onViewProfile={handleViewProfileById} />

        {/* Connection Info Banner */}
        <ConnectionBanner />

        {/* Tabs for Mentors and Entrepreneurs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "all" | "mentors" | "entrepreneurs")}>
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="all" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              All
            </TabsTrigger>
            <TabsTrigger value="mentors" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Mentors
            </TabsTrigger>
            <TabsTrigger value="entrepreneurs" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Entrepreneurs
            </TabsTrigger>
          </TabsList>

          {/* Search and Sort Controls */}
          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or company..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch 
                  id="connected-only"
                  checked={showConnectedOnly}
                  onCheckedChange={setShowConnectedOnly}
                />
                <Label htmlFor="connected-only" className="text-sm text-muted-foreground whitespace-nowrap cursor-pointer">
                  Connected Only
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="sort-select" className="text-sm text-muted-foreground whitespace-nowrap">Sort:</Label>
                <Select value={sortBy} onValueChange={(value) => setSortBy(value as 'first_name' | 'last_name' | 'connected_first')}>
                  <SelectTrigger id="sort-select" className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first_name">First Name (A-Z)</SelectItem>
                    <SelectItem value="last_name">Last Name (A-Z)</SelectItem>
                    <SelectItem value="connected_first">Connected First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <TabsContent value="all" className="space-y-6 mt-4">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">
                {searchQuery ? "Search Results" : "All Members"}
              </h2>

              {membersLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : sortedMembers.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {sortedMembers.map((member) => (
                      <MemberCard
                        key={member.id}
                        member={member}
                        onRequestConnection={() => handleRequestConnection(member)}
                        onAdminEdit={() => handleAdminEdit(member)}
                        onViewProfile={() => handleViewProfile(member)}
                        onNavigateToProfile={() => navigate("/profile")}
                        canRequest={member.id !== user?.id}
                        showContactInfo={canViewContactInfo(member.role)}
                        isAdmin={isAdmin}
                        connectionStatus={getConnectionStatus(member.id).status}
                        isOwnProfile={member.id === user?.id}
                      />
                    ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <User className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No members found</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="mentors" className="space-y-6 mt-4">
            {/* Request by Topic Banner for Entrepreneurs */}
            {isEntrepreneur && !searchQuery && (
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Sparkles className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Not sure which mentor to request?</p>
                      <p className="text-sm text-muted-foreground">Request mentorship by topic and our team can help connect you.</p>
                    </div>
                  </div>
                  <Button onClick={() => setShowTopicRequestDialog(true)}>
                    Request by Topic
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Suggested Matches for Entrepreneurs */}
            {isEntrepreneur && suggestedMatches && suggestedMatches.length > 0 && !searchQuery && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-accent" />
                  <h2 className="text-lg font-semibold">Suggested Matches</h2>
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {suggestedMatches.slice(0, 3).map((member: any) => (
                    <MemberCard
                      key={member.id}
                      member={member}
                      onRequestConnection={() => handleRequestConnection(member)}
                      onAdminEdit={() => handleAdminEdit(member)}
                      onViewProfile={() => handleViewProfile(member)}
                      onNavigateToProfile={() => navigate("/profile")}
                      canRequest={member.id !== user?.id}
                      showContactInfo={false}
                      isAdmin={isAdmin}
                      isSuggested
                      connectionStatus={getConnectionStatus(member.id).status}
                      isOwnProfile={member.id === user?.id}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* All Mentors */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">
                {searchQuery ? "Search Results" : "All Mentors"}
              </h2>

              {membersLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : sortedMembers.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {sortedMembers.map((member) => (
                    <MemberCard
                      key={member.id}
                      member={member}
                      onRequestConnection={() => handleRequestConnection(member)}
                      onAdminEdit={() => handleAdminEdit(member)}
                      onViewProfile={() => handleViewProfile(member)}
                      onNavigateToProfile={() => navigate("/profile")}
                      canRequest={member.id !== user?.id}
                      showContactInfo={canViewContactInfo("mentor")}
                      isAdmin={isAdmin}
                      connectionStatus={getConnectionStatus(member.id).status}
                      isOwnProfile={member.id === user?.id}
                    />
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <User className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No mentors found</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="entrepreneurs" className="space-y-6 mt-4">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">
                {searchQuery ? "Search Results" : "All Entrepreneurs"}
              </h2>

              {membersLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : sortedMembers.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {sortedMembers.map((member) => (
                    <MemberCard
                      key={member.id}
                      member={member}
                      onRequestConnection={() => handleRequestConnection(member)}
                      onAdminEdit={() => handleAdminEdit(member)}
                      onViewProfile={() => handleViewProfile(member)}
                      onNavigateToProfile={() => navigate("/profile")}
                      canRequest={member.id !== user?.id}
                      showContactInfo={canViewContactInfo("entrepreneur")}
                      isAdmin={isAdmin}
                      connectionStatus={getConnectionStatus(member.id).status}
                      isOwnProfile={member.id === user?.id}
                    />
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Briefcase className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No entrepreneurs found</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Connection Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Connection</DialogTitle>
            <DialogDescription>
              Send a connection request to {selectedMember?.full_name}. Explain why you'd like to connect.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarImage src={getStorageUrl("avatars", selectedMember?.avatar_url) || undefined} />
                <AvatarFallback>{selectedMember?.full_name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium"><UserName userId={selectedMember?.id} name={selectedMember?.full_name} /></p>
                <p className="text-sm text-muted-foreground capitalize">{selectedMember?.role}</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Your Message</Label>
              <Textarea
                id="message"
                placeholder="Explain why you'd like to connect and what you hope to learn..."
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitRequest}
              disabled={createConnectionMutation.isPending}
              className="bg-accent hover:bg-accent/90"
            >
              {createConnectionMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile Detail Dialog - for all users */}
      <ProfileDetailDialog
        member={viewingMember}
        open={showProfileDialog}
        onOpenChange={setShowProfileDialog}
        showAllFields={isAdmin}
        showContactInfo={viewingMember ? canViewContactInfo(viewingMember.role) : false}
        onEdit={isAdmin ? () => {
          setShowProfileDialog(false);
          if (viewingMember) {
            setEditingMember(viewingMember);
            setShowEditDialog(true);
          }
        } : undefined}
        canRequestConnection={viewingMember ? (
          viewingMember.id !== user?.id
        ) : false}
        connectionStatus={viewingMember ? getConnectionStatus(viewingMember.id).status : null}
        onRequestConnection={() => {
          if (viewingMember) {
            setShowProfileDialog(false);
            handleRequestConnection(viewingMember);
          }
        }}
        canRequestMentorship={
          isEntrepreneur && 
          (viewingMember?.role === "mentor" || viewingMember?.is_mentor === true) && 
          viewingMember?.id !== user?.id && 
          !existingMentorshipRequest
        }
        hasCompletedFoundations={foundationsCompleted}
        isEnrolledInFoundations={isEnrolledInFoundations}
        foundationsProgress={foundationsProgress}
        foundationsCourseLink={foundationsCourseLink}
        onRequestMentorship={() => {
          if (viewingMember) {
            handleRequestMentorship(viewingMember);
          }
        }}
        mentorshipRequestStatus={existingMentorshipRequest?.status ?? null}
        mentorshipRequestId={existingMentorshipRequest?.id ?? null}
        onCancelMentorshipRequest={() => {
          queryClient.invalidateQueries({ queryKey: ["existing-mentorship-request"] });
        }}
      />

      {/* Mentorship Request Dialog */}
      {mentorshipTarget && (
        <MentorshipRequestDialog
          open={showMentorshipDialog}
          onOpenChange={(open) => {
            setShowMentorshipDialog(open);
            if (!open) setMentorshipTarget(null);
          }}
          mentor={{
            id: mentorshipTarget.id,
            full_name: mentorshipTarget.full_name,
            avatar_url: mentorshipTarget.avatar_url,
          }}
        />
      )}



      <RequestMentorshipByTopicDialog
        open={showTopicRequestDialog}
        onOpenChange={setShowTopicRequestDialog}
      />

      {/* Admin Edit Dialog */}
      <AdminEditDialog
        member={editingMember}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onSave={(profileData, mentorData, entrepreneurData) => {
          if (editingMember) {
            updateProfileMutation.mutate({
              profileId: editingMember.id,
              profileData,
              mentorData,
              entrepreneurData,
            });
          }
        }}
        isSaving={updateProfileMutation.isPending}
      />
      </>
      )}
    </DashboardLayout>
  );
}

function MemberCard({
  member,
  onRequestConnection,
  onAdminEdit,
  onViewProfile,
  onNavigateToProfile,
  canRequest,
  showContactInfo = false,
  isAdmin = false,
  isSuggested = false,
  connectionStatus = null,
  isOwnProfile = false,
}: {
  member: MemberProfile;
  onRequestConnection: () => void;
  onAdminEdit?: () => void;
  onViewProfile?: () => void;
  onNavigateToProfile?: () => void;
  canRequest: boolean;
  showContactInfo?: boolean;
  isAdmin?: boolean;
  isSuggested?: boolean;
  connectionStatus?: string | null;
  isOwnProfile?: boolean;
}) {
  return (
    <Card 
      className={`card-hover cursor-pointer ${isSuggested ? "border-accent/50" : ""}`}
      onClick={() => onViewProfile?.()}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <Avatar className="h-12 w-12">
            <AvatarImage src={getStorageUrl("avatars", member.avatar_url) || undefined} />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {member.full_name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base truncate"><UserName userId={member.id} name={member.full_name} /></CardTitle>
              {member.is_mentor && member.is_entrepreneur ? (
                <Badge variant="outline" className="text-xs">
                  Mentor + Entrepreneur
                </Badge>
              ) : (
                <Badge
                  variant={member.role === "mentor" ? "default" : "secondary"}
                  className="text-xs capitalize"
                >
                  {member.role}
                </Badge>
              )}
              {isAdmin && member.is_published === false && (
                <Badge variant="outline" className="text-xs text-orange-500 border-orange-500">
                  Unpublished
                </Badge>
              )}
            </div>
            <CardDescription className="flex items-center gap-1">
              {member.role === "mentor" ? (
                <>
                  <User className="h-3 w-3" />
                  {member.mentor_profile?.years_experience
                    ? `${member.mentor_profile.years_experience}+ years exp.`
                    : "Mentor"}
                </>
              ) : (
                <>
                  <Briefcase className="h-3 w-3" />
                  {member.entrepreneur_profile?.company_name || "Entrepreneur"}
                </>
              )}
            </CardDescription>
          </div>
          {isSuggested && (
            <Badge variant="secondary" className="bg-accent/10 text-accent text-xs">
              <Sparkles className="h-3 w-3 mr-1" />
              Match
            </Badge>
          )}
          {isAdmin && onAdminEdit && (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={(e) => { e.stopPropagation(); onAdminEdit(); }} 
              className="h-8 w-8"
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {member.bio && (
          <p className="text-sm text-muted-foreground line-clamp-2">{member.bio}</p>
        )}

        {/* Contact Information - only shown based on role permissions */}
        {showContactInfo && (member.email || member.linkedin_url) ? (
          <div className="flex flex-wrap gap-2 pt-1">
            {member.email && (
              <a
                href={`mailto:${member.email}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                <Mail className="h-3 w-3" />
                {member.email}
              </a>
            )}
            {member.linkedin_url && (
              <a
                href={member.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                <Linkedin className="h-3 w-3" />
                LinkedIn
              </a>
            )}
          </div>
        ) : !showContactInfo && member.role === "mentor" ? (
          <div className="flex items-center gap-1 text-xs text-muted-foreground pt-1">
            <Lock className="h-3 w-3" />
            <span>Contact visible after connection</span>
          </div>
        ) : null}

        {member.industries && member.industries.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {member.industries.slice(0, 2).map((industry, i) => (
              <Badge key={i} variant="outline" className="text-xs">
                {industry.name}
              </Badge>
            ))}
            {member.industries.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{member.industries.length - 2}
              </Badge>
            )}
          </div>
        )}

        {member.skills && member.skills.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {member.skills.slice(0, 3).map((skill, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                {skill.name}
              </Badge>
            ))}
            {member.skills.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{member.skills.length - 3}
              </Badge>
            )}
          </div>
        )}

        {isOwnProfile ? (
          <Button
            onClick={(e) => { e.stopPropagation(); onNavigateToProfile?.(); }}
            className="w-full mt-2"
            variant="secondary"
            size="sm"
          >
            <User className="h-4 w-4 mr-2" />
            My Profile
          </Button>
        ) : connectionStatus ? (
          <div className="w-full mt-2" onClick={(e) => e.stopPropagation()}>
            {connectionStatus === "accepted" ? (
              <ConnectionButton
                targetUserId={member.id}
                targetUserRole={(member.role === "mentor" || member.is_mentor === true) ? "mentor" : "entrepreneur"}
                size="sm"
                showRemoveOption
                className="w-full"
              />
            ) : (
              <Badge variant="outline" className="w-full justify-center py-2 text-muted-foreground">
                <Clock className="h-3 w-3 mr-1" />
                Request Pending
              </Badge>
            )}
          </div>
        ) : canRequest ? (
          <Button
            onClick={(e) => { e.stopPropagation(); onRequestConnection(); }}
            className="w-full mt-2 bg-accent hover:bg-accent/90"
            size="sm"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Request Connection
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

// Admin Edit Dialog Component
function AdminEditDialog({
  member,
  open,
  onOpenChange,
  onSave,
  isSaving,
}: {
  member: MemberProfile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (profileData: any, mentorData?: any, entrepreneurData?: any) => void;
  isSaving: boolean;
}) {
  const [formData, setFormData] = useState<any>({});

  useEffect(() => {
    if (member) {
      setFormData({
        full_name: member.full_name || "",
        email: member.email || "",
        bio: member.bio || "",
        linkedin_url: member.linkedin_url || "",
        parish: member.parish || "",
        phone: member.phone || "",
        phone_type: member.phone_type || "",
        referred_by: member.referred_by || "",
        gender: member.gender || "",
        age_range: member.age_range || "",
        has_device: member.has_device || "",
        education_level: member.education_level || "",
        is_published: member.is_published ?? false,
        is_verified: member.is_verified ?? false,
        is_onboarded: member.is_onboarded ?? false,
        // Mentor fields
        years_experience: member.mentor_profile?.years_experience || "",
        professional_background: member.mentor_profile?.professional_background || "",
        career_highlights: member.mentor_profile?.career_highlights || "",
        availability_notes: member.mentor_profile?.availability_notes || "",
        // Entrepreneur fields
        company_name: member.entrepreneur_profile?.company_name || "",
        company_description: member.entrepreneur_profile?.company_description || "",
        startup_stage: member.entrepreneur_profile?.startup_stage || "",
        target_market: member.entrepreneur_profile?.target_market || "",
        funding_status: member.entrepreneur_profile?.funding_status || "",
        specific_challenges: member.entrepreneur_profile?.specific_challenges || "",
      });
    }
  }, [member]);

  const handleSave = () => {
    const profileData = {
      full_name: formData.full_name,
      bio: formData.bio,
      linkedin_url: formData.linkedin_url,
      parish: formData.parish,
      phone: formData.phone,
      phone_type: formData.phone_type,
      referred_by: formData.referred_by,
      gender: formData.gender,
      age_range: formData.age_range,
      has_device: formData.has_device,
      education_level: formData.education_level,
      is_published: formData.is_published,
      is_verified: formData.is_verified,
      is_onboarded: formData.is_onboarded,
    };

    const mentorData = member?.role === "mentor" ? {
      years_experience: formData.years_experience ? parseInt(formData.years_experience) : null,
      professional_background: formData.professional_background,
      career_highlights: formData.career_highlights,
      availability_notes: formData.availability_notes,
    } : undefined;

    const entrepreneurData = member?.role === "entrepreneur" ? {
      company_name: formData.company_name || null,
      company_description: formData.company_description || null,
      startup_stage: formData.startup_stage || null,
      target_market: formData.target_market || null,
      funding_status: formData.funding_status || null,
      specific_challenges: formData.specific_challenges || null,
    } : undefined;

    onSave(profileData, mentorData, entrepreneurData);
  };

  if (!member) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Edit Profile: <UserName userId={member.id} name={member.full_name} /></DialogTitle>
          <DialogDescription>Admin view - all fields editable</DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={formData.full_name || ""} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={formData.email || ""} disabled className="bg-muted" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea value={formData.bio || ""} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} rows={3} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Parish</Label>
                <Input value={formData.parish || ""} onChange={(e) => setFormData({ ...formData, parish: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={formData.phone || ""} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Gender</Label>
                <Input value={formData.gender || ""} onChange={(e) => setFormData({ ...formData, gender: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Age Range</Label>
                <Input value={formData.age_range || ""} onChange={(e) => setFormData({ ...formData, age_range: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Education Level</Label>
                <Select 
                  value={formData.education_level || ""} 
                  onValueChange={(value) => setFormData({ ...formData, education_level: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select education level" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {EDUCATION_LEVEL_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Device Access</Label>
                <Input value={formData.has_device || ""} onChange={(e) => setFormData({ ...formData, has_device: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Referred By</Label>
                <Input value={formData.referred_by || ""} onChange={(e) => setFormData({ ...formData, referred_by: e.target.value })} />
              </div>
            </div>

            {member.role === "mentor" && (
              <>
                <h4 className="font-semibold pt-2">Mentor Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Years Experience</Label>
                    <Input type="number" value={formData.years_experience || ""} onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Professional Background</Label>
                  <Textarea value={formData.professional_background || ""} onChange={(e) => setFormData({ ...formData, professional_background: e.target.value })} rows={2} />
                </div>
              </>
            )}

            {member.role === "entrepreneur" && (
              <>
                <h4 className="font-semibold pt-2">Company Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input value={formData.company_name || ""} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Company Stage</Label>
                    <Select 
                      value={formData.startup_stage || ""} 
                      onValueChange={(value) => setFormData({ ...formData, startup_stage: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select stage" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPANY_STAGE_OPTIONS.map((stage) => (
                          <SelectItem key={stage.value} value={stage.value}>
                            {stage.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Company Description</Label>
                  <Textarea value={formData.company_description || ""} onChange={(e) => setFormData({ ...formData, company_description: e.target.value })} rows={2} />
                </div>
              </>
            )}

            <div className="flex items-center gap-4 pt-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={formData.is_published || false} onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })} />
                Published
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={formData.is_verified || false} onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })} />
                Verified
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={formData.is_onboarded || false} onChange={(e) => setFormData({ ...formData, is_onboarded: e.target.checked })} />
                Onboarded
              </label>
            </div>
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSaving} className="bg-accent hover:bg-accent/90">
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
